import { useEffect, useRef, useState, useCallback } from 'react';

interface CommentData {
  commentid: number;
  roomid: string;
  text: string;
  nickname: string;
  userid?: string;
  createdate: string;
  screenTimestamp: number;
  positionX?: number;
  positionY?: number;
  color?: string;
  fontSize?: number;
  isModerated: number;
}

interface WebSocketMessage {
  type: string;
  roomid: string;
  data: any;
  timestamp: number;
  success: boolean;
  message?: string;
}

interface UseWebSocketReturn {
  isConnected: boolean;
  comments: CommentData[];
  sendMessage: (message: any) => void;
  clearComments: () => void;
}

const useWebSocket = (roomId: string): UseWebSocketReturn => {
  const [isConnected, setIsConnected] = useState(false);
  const [comments, setComments] = useState<CommentData[]>([]);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef<number>(0);

  const connect = useCallback(() => {
    if (!roomId) return;

    try {
      // 環境変数からベースURLを取得し、WebSocket URLを構築
      const baseUrl = process.env.REACT_APP_URL || 'http://localhost:8081';
      const wsBaseUrl = baseUrl.replace('http://', 'ws://').replace('https://', 'wss://');
      const wsUrl = `${wsBaseUrl}/ws/comment?roomid=${encodeURIComponent(roomId)}`;
      console.log('[WebSocket] 接続試行:', wsUrl);
      console.log('[WebSocket] baseUrl:', baseUrl);
      console.log('[WebSocket] wsBaseUrl:', wsBaseUrl);
      console.log('[WebSocket] roomId:', roomId);
      
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[WebSocket] 接続成功:', wsUrl);
        setIsConnected(true);
        reconnectAttempts.current = 0;
        
        // 接続後にルーム参加メッセージを送信
        const joinMessage = {
          type: 'JOIN_ROOM',
          roomid: roomId
        };
        console.log('[WebSocket] ルーム参加メッセージ送信:', joinMessage);
        ws.send(JSON.stringify(joinMessage));
        
        // 最近のコメントを取得（初回のみ）
        // setTimeout(() => {
        //   const recentMessage = {
        //     type: 'GET_RECENT_COMMENTS',
        //     roomid: roomId
        //   };
        //   console.log('[WebSocket] 最近コメント取得メッセージ送信:', recentMessage);
        //   ws.send(JSON.stringify(recentMessage));
        // }, 500);
      };

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          console.log('[WebSocket] メッセージ受信:', message);

          switch (message.type) {
            case 'NEW_COMMENT':
              if (message.data && message.success) {
                setComments(prev => {
                  // 重複チェック: 同じcommentidがすでに存在するかチェック
                  const isAlreadyExists = prev.some(comment => comment.commentid === message.data.commentid);
                  if (!isAlreadyExists) {
                    return [...prev, message.data];
                  }
                  return prev; // 重複の場合は既存のstateを返す
                });
              }
              break;
            case 'COMMENT_DELETED':
              if (message.data && message.success) {
                setComments(prev => prev.filter(comment => comment.commentid !== message.data));
              }
              break;
            case 'RECENT_COMMENTS':
              if (message.data && message.success && Array.isArray(message.data)) {
                const approvedComments = message.data.filter((comment: CommentData) => comment.isModerated === 1);
                setComments(approvedComments);
              }
              break;
            case 'ROOM_JOINED':
              console.log('[WebSocket] ルーム参加確認:', message.roomid);
              break;
            case 'ERROR':
              console.error('[WebSocket] エラー:', message.message);
              break;
            default:
              console.log('[WebSocket] 未知のメッセージタイプ:', message.type);
          }
        } catch (error) {
          console.error('[WebSocket] メッセージ解析エラー:', error);
        }
      };

      ws.onclose = (event) => {
        console.log('[WebSocket] 接続切断:', event.code, event.reason);
        setIsConnected(false);
        wsRef.current = null;

        // 自動再接続（最大5回まで）
        if (reconnectAttempts.current < 5) {
          reconnectAttempts.current += 1;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          
          console.log(`[WebSocket] ${delay}ms後に再接続試行 (${reconnectAttempts.current}/5)`);
          reconnectTimeoutRef.current = setTimeout(connect, delay);
        }
      };

      ws.onerror = (error) => {
        console.error('[WebSocket] エラー詳細:', error);
        console.error('[WebSocket] 接続URL:', wsUrl);
        console.error('[WebSocket] roomId:', roomId);
        setIsConnected(false);
      };

    } catch (error) {
      console.error('[WebSocket] 接続エラー:', error);
      setIsConnected(false);
    }
  }, [roomId]);

  const sendMessage = useCallback((message: any) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    } else {
      console.warn('[WebSocket] 未接続のためメッセージ送信できません');
    }
  }, []);

  const clearComments = useCallback(() => {
    setComments([]);
  }, []);

  useEffect(() => {
    connect();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  // コメントの自動クリーンアップ（30秒経過したコメントを削除）
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      setComments(prev => prev.filter(comment => 
        now - comment.screenTimestamp < 30000
      ));
    }, 5000);

    return () => clearInterval(cleanupInterval);
  }, []);

  return {
    isConnected,
    comments,
    sendMessage,
    clearComments
  };
};

export default useWebSocket;